import * as React from "react"

import { cn } from "../../cn"
import {
  ResizeGrip,
  resizeHandleClassName,
  resizeSeparatorHandlers,
} from "../ResizeGrip/ResizeGrip"

/**
 * Mechanics for the docked Drawer — the width it reserves from the app shell's
 * content region, and the drag handle that resizes it.
 *
 * The dock is deliberately independent of the Sofia dock beside it: the two
 * panels publish separate variables (`--drawer-dock-width` here,
 * `--covi-dock-width` there) and own separate widths, so dragging one never
 * resizes the other. They only meet in the shared cap below, which keeps the
 * two docks together from crowding the page out.
 */

/**
 * How much room a drawer's content asks for when it opens. `sm` matches the
 * Sofia panel's own opening width and suits a single column of detail; `lg` is
 * for panels carrying forms, tables, or code that were unreadable narrower.
 *
 * Only the opening width — the user's drag always wins from there, and each
 * size remembers its own dragged width so widening a form panel never squeezes
 * the compact ones.
 */
const OPENING_WIDTHS: Record<"sm" | "md" | "lg", number> = {
  sm: 360,
  md: 480,
  lg: 672,
}

export type DockSize = keyof typeof OPENING_WIDTHS

/** Narrowest the dock can be dragged. Mirrors the `min-w-[20rem]` floor. */
const MIN_WIDTH = 320
/** Space kept for the content the dock squeezes. Mirrors the `24rem` cap. */
const CONTENT_RESERVE = 384
const WIDTH_STORAGE_KEY = "coval:drawer:inline-dock-width"

/** Width the Sofia dock currently reserves, or 0 when it is closed. */
function sofiaDockWidth(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    "--covi-dock-width",
  )
  const value = Number.parseFloat(raw)
  return Number.isFinite(value) ? value : 0
}

/** Clamp a desired width to [MIN, whatever the page and Sofia leave over]. */
function clampWidth(width: number): number {
  if (typeof window === "undefined") {
    return Math.max(width, MIN_WIDTH)
  }
  const max = Math.max(
    MIN_WIDTH,
    window.innerWidth - sofiaDockWidth() - CONTENT_RESERVE,
  )
  return Math.min(Math.max(width, MIN_WIDTH), max)
}

/**
 * Stored widths, one per size. A stray value of any other shape (including the
 * single number this key held before sizes existed) reads as "no preference"
 * rather than throwing, so the drawer falls back to its opening width.
 */
function loadWidths(): Partial<Record<DockSize, number>> {
  if (typeof window === "undefined") {
    return {}
  }
  try {
    const raw = window.localStorage.getItem(WIDTH_STORAGE_KEY)
    if (!raw) {
      return {}
    }
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) {
      return {}
    }
    const stored: Partial<Record<DockSize, number>> = {}
    for (const size of Object.keys(OPENING_WIDTHS) as DockSize[]) {
      const value = (parsed as Record<string, unknown>)[size]
      if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        stored[size] = value
      }
    }
    return stored
  } catch {
    return {}
  }
}

function loadWidth(size: DockSize): number | null {
  return loadWidths()[size] ?? null
}

function saveWidth(size: DockSize, width: number): void {
  if (typeof window === "undefined") {
    return
  }
  try {
    window.localStorage.setItem(
      WIDTH_STORAGE_KEY,
      JSON.stringify({ ...loadWidths(), [size]: Math.round(width) }),
    )
  } catch {
    // Quota exceeded / private mode — drop silently; width stays in-memory.
  }
}

/**
 * Widths of every currently docked drawer. Nothing stops two independent
 * drawers from being open at once, so the reservation is a registry rather than
 * a single value: the variable tracks the widest one and is only cleared once
 * the last drawer closes, instead of the first one to unmount releasing space
 * still in use.
 */
const dockedWidths = new Map<HTMLElement, number>()

function publishDockWidth() {
  const root = document.documentElement
  if (dockedWidths.size === 0) {
    root.style.removeProperty("--drawer-dock-width")
    return
  }
  root.style.setProperty(
    "--drawer-dock-width",
    `${Math.max(...dockedWidths.values())}px`,
  )
}

/**
 * Publishes the drawer's width as `--drawer-dock-width` on the document root,
 * the same contract the Sofia dock uses with `--covi-dock-width`: the app shell
 * reflows its content region by that much, so the drawer lands between the
 * content and Sofia instead of on top of the content.
 *
 * The width is measured from the element rather than read from the width state,
 * so the CSS caps in `drawerContentVariants` are honored for free — including
 * when Sofia is dragged wider and squeezes the drawer below its stored width.
 * The reservation is released as soon as the drawer closes, so the content
 * re-expands while the panel is still sliding out.
 *
 * Surfaces without the app shell simply ignore the variable — the drawer still
 * docks against the viewport edge, nothing reflows around it.
 */
export function useDockWidthReservation(active: boolean) {
  const [node, setNode] = React.useState<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (!active || !node) {
      return
    }

    const measure = () => {
      dockedWidths.set(node, Math.round(node.getBoundingClientRect().width))
      publishDockWidth()
    }
    measure()
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure)
    observer?.observe(node)
    window.addEventListener("resize", measure)
    return () => {
      observer?.disconnect()
      window.removeEventListener("resize", measure)
      dockedWidths.delete(node)
      publishDockWidth()
    }
  }, [active, node])

  return setNode
}

export interface InlineDockResize {
  /** Current dock width in px, always clamped to the current viewport. */
  width: number
  setWidth: (width: number) => void
  isResizing: boolean
  setResizing: (resizing: boolean) => void
}

/**
 * Owns the dock's width: the opening width, the persisted preference, viewport
 * clamping, and the drag lifecycle. Every drawer docks into the same column but
 * keeps its preference per size, so dragging a wide form panel narrower does
 * not also shrink the compact detail panels.
 */
export function useInlineDockResize(
  active: boolean,
  size: DockSize,
): InlineDockResize {
  // Starts at the opening width for SSR parity, then hydrates the persisted
  // preference once the drawer docks.
  const [width, setWidthState] = React.useState(OPENING_WIDTHS[size])
  const [isResizing, setResizing] = React.useState(false)
  const widthDirtyRef = React.useRef(false)

  React.useEffect(() => {
    if (!active) {
      return
    }
    setWidthState(clampWidth(loadWidth(size) ?? OPENING_WIDTHS[size]))
  }, [active, size])

  const setWidth = React.useCallback((next: number) => {
    widthDirtyRef.current = true
    setWidthState(clampWidth(next))
  }, [])

  React.useEffect(() => {
    const handleResize = () => setWidthState((current) => clampWidth(current))
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Persist only once the drag settles, so a drag writes one value rather than
  // one per pointer move.
  React.useEffect(() => {
    if (!widthDirtyRef.current || isResizing) {
      return
    }
    saveWidth(size, width)
    widthDirtyRef.current = false
  }, [isResizing, size, width])

  // A drag has to track the pointer exactly, so it suppresses the content
  // region's reflow transition for its duration — the same trick the Sofia dock
  // plays with `--covi-dock-transition`.
  React.useEffect(() => {
    const root = document.documentElement
    if (!isResizing) {
      root.style.removeProperty("--drawer-dock-transition")
      return
    }
    root.style.setProperty("--drawer-dock-transition", "none")
    return () => {
      root.style.removeProperty("--drawer-dock-transition")
    }
  }, [isResizing])

  return { width, setWidth, isResizing, setResizing }
}

/**
 * Drag handle on the dock's inner edge. Wears the shared resize grip, so every
 * resizable divider on a page reads as the same affordance.
 */
export function DrawerResizeHandle({
  width,
  setWidth,
  isResizing,
  setResizing,
}: InlineDockResize) {
  const cleanupRef = React.useRef<(() => void) | null>(null)

  React.useEffect(() => {
    return () => cleanupRef.current?.()
  }, [])

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    cleanupRef.current?.()
    setResizing(true)

    // The dock's right edge sits at the Sofia dock, so the pointer's distance
    // from that edge is the width the user is asking for.
    const rightEdge = window.innerWidth - sofiaDockWidth()
    const handleMove = (move: PointerEvent) => {
      setWidth(rightEdge - move.clientX)
    }
    const cleanup = () => {
      setResizing(false)
      window.removeEventListener("pointermove", handleMove)
      window.removeEventListener("pointerup", cleanup)
      window.removeEventListener("pointercancel", cleanup)
      window.removeEventListener("blur", cleanup)
      document.body.style.removeProperty("user-select")
      document.body.style.removeProperty("cursor")
      cleanupRef.current = null
    }

    document.body.style.userSelect = "none"
    document.body.style.cursor = "col-resize"
    window.addEventListener("pointermove", handleMove)
    window.addEventListener("pointerup", cleanup)
    window.addEventListener("pointercancel", cleanup)
    window.addEventListener("blur", cleanup)
    cleanupRef.current = cleanup
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panel"
      aria-valuenow={width}
      aria-valuetext={`${width}px`}
      tabIndex={0}
      data-resizing={isResizing}
      {...resizeSeparatorHandlers({ width, setWidth, onPointerDown })}
      // `left` resolves against the padding box, so -1px puts the handle on the
      // panel's border rather than just inside it.
      className={cn(resizeHandleClassName, "-left-px -translate-x-1/2")}
    >
      <ResizeGrip />
    </div>
  )
}
